import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/misc';
import { AuthLayout } from '@/components/AuthLayout';
import { Turnstile } from '@/components/Turnstile';
import { PasswordInput } from '@/components/PasswordInput';
import { InitialsAvatar } from '@/components/InitialsAvatar';
import { makeClient } from '@/lib/api';
import { track } from '@/lib/analytics';
import { useSession } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { clearRemembered, getRemembered, setRemembered } from '@/lib/remember';

interface LoginUser { name?: string; email: string }

export function Login() {
  const t = useT();
  const [remembered, setRememberedState] = useState(getRemembered());
  const welcome = !!remembered;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0); // bump → Turnstile gera um token fresco
  const [busy, setBusy] = useState(false);
  const { login } = useSession();
  const navigate = useNavigate();

  const useOther = () => { clearRemembered(); setRememberedState(null); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Sem token do captcha o servidor devolveria 403 — evita o envio fadado e força o
    // widget a (re)gerar um token fresco (cobre expiração e captcha bloqueado por adblock).
    if (!token) { toast.error(t('auth.captchaIncomplete')); setCaptchaKey((k) => k + 1); return; }
    setBusy(true);
    try {
      const loginEmail = welcome && remembered ? remembered.email : email;
      const { data, error } = await makeClient().POST('/auth/login', {
        body: { email: loginEmail, password, turnstile_token: token ?? undefined },
      });
      if (error || !data) {
        // O backend usa 403 p/ dois casos distintos — distinguir pelo CODE (não pelo status),
        // senão uma falha de captcha vira "confirme seu email" (confunde o usuário). [[bug]]
        const code = (error as { code?: string } | undefined)?.code;
        toast.error(
          code === 'email_not_verified' ? t('auth.notVerified')
          : code === 'captcha_failed' || code === 'captcha_unavailable' ? t('auth.captchaFailed')
          : t('auth.badCredentials'),
        );
        // O token do captcha é de uso único: descarta e renova p/ a próxima tentativa.
        setToken(null);
        setCaptchaKey((k) => k + 1);
        return;
      }
      const session = data as { token?: string; user?: LoginUser };
      if (!session.token) { toast.error(t('auth.unexpected')); return; }

      // Lembrar de mim: guarda nome/email (nunca a senha).
      if (welcome || remember) {
        const u = session.user;
        setRemembered({ name: u?.name ?? '', email: u?.email ?? loginEmail });
      } else {
        clearRemembered();
      }

      login(session.token);
      track('login', { method: 'password' });
      toast.success(t('auth.welcomeBack'));
      // Volta para onde a pessoa estava quando a sessão caiu (?next=), se houver.
      const next = new URLSearchParams(window.location.search).get('next');
      navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout subtitle={welcome ? t('auth.welcomeSubtitle') : t('auth.loginSubtitle')}>
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        {welcome && remembered ? (
          <>
            <p className="text-center text-xl font-bold">{t('auth.welcome')}</p>
            <div className="mt-4 flex flex-col items-center gap-2">
              <InitialsAvatar name={remembered.name} email={remembered.email} size={64} />
              <p className="font-semibold">{remembered.name || remembered.email}</p>
              <p className="text-sm text-muted-foreground">{remembered.email}</p>
            </div>
            <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <PasswordInput id="password" autoComplete="current-password" autoFocus required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.passwordPlaceholder')} />
                <Link to="/forgot" className="self-end text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">{t('auth.forgotLink')}</Link>
              </div>
              <Turnstile onToken={setToken} resetKey={captchaKey} />
              <Button type="submit" disabled={busy} className="w-full">
                {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />}
                {t('auth.enter')}
              </Button>
            </form>
            <button type="button" onClick={useOther} className="mt-4 w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground">
              {t('auth.notYou')}
            </button>
          </>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@empresa.com" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <PasswordInput id="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.passwordPlaceholder')} />
              <Link to="/forgot" className="self-end text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">{t('auth.forgotLink')}</Link>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="size-4 rounded border-input accent-[#E0780C]" />
              {t('auth.remember')}
            </label>
            <Turnstile onToken={setToken} resetKey={captchaKey} />
            <Button type="submit" disabled={busy} className="w-full">
              {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />}
              {t('auth.enter')}
            </Button>
          </form>
        )}
      </div>

      <Link
        to="/signup"
        className="group mt-4 flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4 transition-colors hover:border-primary/50 hover:bg-primary/10"
      >
        <span className="flex flex-col text-left">
          <span className="text-xs text-muted-foreground">{t('auth.noAccount')}</span>
          <span className="font-semibold text-primary">{t('auth.signupCta')}</span>
        </span>
        <ArrowRight className="size-5 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
      </Link>
    </AuthLayout>
  );
}
